// ===========================================
// Custom SMS & WhatsApp Hooks
// ===========================================
// React Query hooks for custom SMS/WhatsApp campaigns feature

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createCustomSmsCampaign,
  getCustomSmsCampaigns,
  getCustomSmsCampaign,
  updateCustomSmsCampaign,
  deleteCustomSmsCampaign,
  duplicateCustomSmsCampaign,
  scheduleCustomSmsCampaign,
  cancelScheduledSmsCampaign,
  sendCustomSmsCampaign,
  sendTestSms,
  getSmsCampaignRecipients,
  getSmsCampaignStats,
  getSmsCampaignAnalyticsRecords,
} from '../api/customSms.api';
import {
  CreateCustomSmsCampaignData,
  UpdateCustomSmsCampaignData,
  ScheduleCustomSmsCampaignData,
  TestSmsData,
  CustomSmsCampaignFilters,
} from '../types/customSms.types';

// ===========================================
// QUERY KEY FACTORY
// ===========================================

export const customSmsKeys = {
  all: ['customSms'] as const,
  lists: () => [...customSmsKeys.all, 'lists'] as const,
  list: (filters?: CustomSmsCampaignFilters) => [...customSmsKeys.lists(), filters] as const,
  details: () => [...customSmsKeys.all, 'details'] as const,
  detail: (campaignId: string) => [...customSmsKeys.details(), campaignId] as const,
  recipients: (campaignId: string) => [...customSmsKeys.all, 'recipients', campaignId] as const,
  analytics: () => [...customSmsKeys.all, 'analytics'] as const,
  stats: (campaignId: string) => [...customSmsKeys.analytics(), 'stats', campaignId] as const,
  records: (campaignId: string, page?: number, limit?: number) =>
    [...customSmsKeys.analytics(), 'records', campaignId, { page, limit }] as const,
};

// ===========================================
// CRUD HOOKS
// ===========================================

export function useCustomSmsCampaigns(filters?: CustomSmsCampaignFilters) {
  return useQuery({
    queryKey: customSmsKeys.list(filters),
    queryFn: () => getCustomSmsCampaigns(filters),
  });
}

export function useCustomSmsCampaign(campaignId: string) {
  return useQuery({
    queryKey: customSmsKeys.detail(campaignId),
    queryFn: () => getCustomSmsCampaign(campaignId),
    enabled: !!campaignId && campaignId !== 'new',
  });
}

export function useCreateCustomSmsCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCustomSmsCampaignData) => createCustomSmsCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customSmsKeys.lists() });
    },
  });
}

export function useUpdateCustomSmsCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId,
      data,
    }: {
      campaignId: string;
      data: UpdateCustomSmsCampaignData;
    }) => updateCustomSmsCampaign(campaignId, data),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: customSmsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customSmsKeys.detail(campaignId) });
      queryClient.invalidateQueries({ queryKey: customSmsKeys.recipients(campaignId) });
    },
  });
}

export function useDeleteCustomSmsCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => deleteCustomSmsCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customSmsKeys.lists() });
    },
  });
}

export function useDuplicateCustomSmsCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => duplicateCustomSmsCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customSmsKeys.lists() });
    },
  });
}

// ===========================================
// SCHEDULING HOOKS
// ===========================================

export function useScheduleCustomSmsCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      campaignId,
      data,
    }: {
      campaignId: string;
      data: ScheduleCustomSmsCampaignData;
    }) => scheduleCustomSmsCampaign(campaignId, data),
    onSuccess: (_, { campaignId }) => {
      queryClient.invalidateQueries({ queryKey: customSmsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customSmsKeys.detail(campaignId) });
    },
  });
}

export function useCancelScheduledSmsCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => cancelScheduledSmsCampaign(campaignId),
    onSuccess: (_, campaignId) => {
      queryClient.invalidateQueries({ queryKey: customSmsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customSmsKeys.detail(campaignId) });
    },
  });
}

// ===========================================
// SENDING HOOKS
// ===========================================

export function useSendCustomSmsCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => sendCustomSmsCampaign(campaignId),
    onSuccess: (_, campaignId) => {
      queryClient.invalidateQueries({ queryKey: customSmsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: customSmsKeys.detail(campaignId) });
      queryClient.invalidateQueries({ queryKey: customSmsKeys.stats(campaignId) });
    },
  });
}

export function useSendTestSms() {
  return useMutation({
    mutationFn: ({
      campaignId,
      data,
    }: {
      campaignId: string;
      data: TestSmsData;
    }) => sendTestSms(campaignId, data),
  });
}

// ===========================================
// RECIPIENT HOOKS
// ===========================================

export function useSmsCampaignRecipients(campaignId: string) {
  return useQuery({
    queryKey: customSmsKeys.recipients(campaignId),
    queryFn: () => getSmsCampaignRecipients(campaignId),
    enabled: !!campaignId && campaignId !== 'new',
  });
}

// ===========================================
// ANALYTICS HOOKS
// ===========================================

export function useSmsCampaignStats(campaignId: string) {
  return useQuery({
    queryKey: customSmsKeys.stats(campaignId),
    queryFn: () => getSmsCampaignStats(campaignId),
    enabled: !!campaignId,
  });
}

export function useSmsCampaignAnalyticsRecords(
  campaignId: string,
  params?: { page?: number; limit?: number }
) {
  return useQuery({
    queryKey: customSmsKeys.records(campaignId, params?.page, params?.limit),
    queryFn: () => getSmsCampaignAnalyticsRecords(campaignId, params),
    enabled: !!campaignId,
  });
}
